import {
  AppHomeActor,
  AppHomeAuditEntry,
  AppHomeAuditSnapshots,
  AppHomeConfigEnvelope,
  AppHomePreview,
  BackgroundSchedule,
  ButtonBar,
  CardGreeting,
  CardStack,
  HomeBlocks,
  HomeButton,
  HomeConfig,
  HomeMedia,
  PublishRejection,
} from '../app-home-config.types';

/*
 * Shaped like a real one: the distribution, then the client id, then the folder
 * the object type maps to, then a name the media service made unique. A fixture
 * url that reads `/daybreak/home/default.jpg` teaches a path structure that does
 * not exist, and hides that nothing in the app composes these.
 */
const MEDIA_BASE =
  'https://media.incentivio.com/1b63163d-1aba-4944-bbc1-ebc495d9dc5c/ac';

export const imageMediaFixture = (
  overrides?: Partial<HomeMedia>,
): HomeMedia => ({
  type: 'IMAGE',
  altText: 'Warm cafe interior with morning light over the counter',
  image: { url: `${MEDIA_BASE}/default_add025a701cc4c74ab66aebbb6b4f777.jpg` },
  ...overrides,
});

export const videoMediaFixture = (
  overrides?: Partial<HomeMedia>,
): HomeMedia => ({
  type: 'VIDEO',
  altText: 'Snow-dusted coffee cups with holiday greenery',
  video: {
    url: `${MEDIA_BASE}/holiday_4e70d092637442b48b86d6e7d059c415.mp4`,
    poster: `${MEDIA_BASE}/holiday-poster_c7cdb06a4c294cf9bd2b4a8a4903dfa7.jpg`,
  },
  ...overrides,
});

/** A one-time event window. */
export const eventScheduleFixture = (
  overrides?: Partial<BackgroundSchedule>,
): BackgroundSchedule => ({
  id: 'sch_8Kd2Qm',
  name: 'Holiday season',
  mode: 'event',
  start: '2026-12-01T00:00',
  end: '2026-12-26T23:59',
  noEnd: false,
  days: [],
  limitDayparts: false,
  dayparts: [],
  media: videoMediaFixture(),
  ...overrides,
});

/**
 * A repeating window narrowed to two dayparts. Bounded so it does not collide
 * with the holiday event above — an open-ended repeating window would overlap
 * every later schedule that shares a weekday and daypart with it.
 */
export const weekdayScheduleFixture = (
  overrides?: Partial<BackgroundSchedule>,
): BackgroundSchedule => ({
  id: 'sch_Lp04Rt',
  name: 'Weekend brunch',
  mode: 'weekday',
  start: '2026-06-01T00:00',
  end: '2026-08-31T23:59',
  noEnd: false,
  days: ['sat', 'sun'],
  limitDayparts: true,
  dayparts: ['midMorning', 'lunch'],
  media: imageMediaFixture({
    altText: 'Brunch spread on a sunlit patio table',
  }),
  ...overrides,
});

/** An open-ended window, for the cases that need one. */
export const openEndedScheduleFixture = (
  overrides?: Partial<BackgroundSchedule>,
): BackgroundSchedule =>
  weekdayScheduleFixture({
    id: 'sch_NoEnd',
    name: 'Always-on weekends',
    end: null,
    noEnd: true,
    ...overrides,
  });

/* ---------------------------------------------------------------- card feed */

export const greetingCardFixture = (
  overrides?: Partial<CardGreeting>,
): CardGreeting => ({
  id: 'crd_Greeting',
  type: 'greeting',
  line1: 'Good morning',
  line2: 'What are you craving today?',
  showName: true,
  comma: true,
  showBalance: true,
  ...overrides,
});

export const buttonFixture = (overrides?: Partial<HomeButton>): HomeButton => ({
  id: 'btn_Order',
  label: 'Order now',
  style: 'primary',
  destination: 'ordering',
  externalUrl: null,
  ...overrides,
});

export const buttonBarFixture = (
  overrides?: Partial<ButtonBar>,
): ButtonBar => ({
  colorStyle: 'brand',
  rows: [
    { id: 'row_Primary', buttons: [buttonFixture()] },
    {
      id: 'row_Secondary',
      buttons: [
        buttonFixture({
          id: 'btn_Rewards',
          label: 'Rewards',
          style: 'secondary',
          destination: 'loyalty',
        }),
        buttonFixture({
          id: 'btn_Locations',
          label: 'Locations',
          style: 'secondary',
          destination: 'location',
        }),
      ],
    },
  ],
  ...overrides,
});

/**
 * A signed-in feed with something of every shape in it: live member data, an
 * operator-authored promo carrying a timed campaign, and a carousel mixing types.
 * Enough that the editor has something to render rather than an empty state.
 */
export const signedInStackFixture = (
  overrides?: Partial<CardStack>,
): CardStack => ({
  cards: [
    greetingCardFixture(),
    {
      id: 'crd_Loyalty',
      type: 'loyalty',
      colorStyle: 'brand',
      displayMode: 'next',
      layout: 'medium',
      imageSource: 'reward',
      customImage: null,
      destination: 'loyalty',
      copy: {
        next: {
          headline: 'Just [POINTS_TO_REWARD] points from a [REWARD_NAME]',
          subtext: 'You have [POINTS] points. Almost there.',
        },
        nextTier: {
          headline: 'You are [POINTS_TO_TIER] points from [TIER_NAME]',
          subtext: null,
        },
        achieved: {
          headline: 'You have enough points for [REWARD_NAME]',
          subtext: 'Redeem it at checkout on your next visit.',
        },
      },
    },
    {
      id: 'crd_Promo',
      type: 'image',
      image: {
        url: `${MEDIA_BASE}/summer-cold-brew_5f2a1c8e4d3b4a9c8e7f6d5c4b3a2918.jpg`,
        altText: 'Iced cold brew on a sunlit table',
      },
      height: 'double',
      interaction: 'button',
      buttonLabel: 'See the menu',
      buttonAlign: 'left',
      destination: 'ordering',
      externalUrl: null,
      schedules: [
        {
          id: 'tls_Holiday',
          mode: 'event',
          start: '2026-12-01T00:00',
          end: '2026-12-26T23:59',
          noEnd: false,
          days: [],
          limitDayparts: false,
          dayparts: [],
          image: {
            url: `${MEDIA_BASE}/holiday-promo_a1b2c3d4e5f64718293a4b5c6d7e8f90.jpg`,
            altText: 'Snow-dusted coffee cups with holiday greenery',
          },
          interaction: 'tap',
          buttonLabel: null,
          buttonAlign: 'center',
          destination: 'external',
          externalUrl: 'https://daybreak.coffee/holiday',
          colorStyle: 'brand',
          height: 'double',
        },
      ],
    },
    {
      id: 'crd_Carousel',
      type: 'multi',
      height: 'double',
      autoAdvance: true,
      slides: [
        {
          id: 'sld_Offers',
          type: 'offers',
          on: true,
          fields: {
            colorStyle: 'white',
            displayMode: 'four',
            showButton: true,
            buttonLabel: 'Order now',
            autoAdvance: false,
          },
        },
        {
          id: 'sld_LastOrder',
          type: 'lastOrder',
          on: true,
          fields: { colorStyle: 'white', autoAdvance: false },
        },
      ],
    },
    {
      id: 'crd_Rewards',
      type: 'rewards',
      headline: 'Earn points in-store',
      caption: 'Show this code at the register.',
    },
  ],
  bottomButtons: buttonBarFixture(),
  ...overrides,
});

/** Four card types are available here, so this one stays to what a guest may see. */
export const signedOutStackFixture = (
  overrides?: Partial<CardStack>,
): CardStack => ({
  cards: [
    greetingCardFixture({
      id: 'crd_GreetingOut',
      line1: 'Welcome to Daybreak',
      line2: 'Sign in to start earning rewards',
      showName: false,
      comma: false,
      showBalance: false,
    }),
    {
      id: 'crd_JoinHero',
      type: 'image',
      image: {
        url: `${MEDIA_BASE}/join-rewards-hero_9e8d7c6b5a4938271605f4e3d2c1b0a9.jpg`,
        altText: 'Latte art with the Daybreak Rewards logo',
      },
      height: 'triple',
      interaction: 'button',
      buttonLabel: 'Join Daybreak Rewards',
      buttonAlign: 'center',
      destination: 'signUp',
      externalUrl: null,
      schedules: [],
    },
  ],
  bottomButtons: {
    colorStyle: 'brand',
    rows: [
      { id: 'row_OrderOut', buttons: [buttonFixture({ id: 'btn_OrderOut' })] },
      {
        id: 'row_SignUpOut',
        buttons: [
          buttonFixture({
            id: 'btn_SignUpOut',
            label: 'Sign up',
            style: 'secondary',
            destination: 'signUp',
          }),
        ],
      },
    ],
  },
  ...overrides,
});

export const homeBlocksFixture = (
  overrides?: Partial<HomeBlocks>,
): HomeBlocks => ({
  signedIn: signedInStackFixture(),
  signedOut: signedOutStackFixture(),
  ...overrides,
});

export const homeConfigFixture = (
  overrides?: Partial<HomeConfig>,
): HomeConfig => ({
  background: {
    default: { media: imageMediaFixture() },
    schedules: [eventScheduleFixture(), weekdayScheduleFixture()],
  },
  blocks: homeBlocksFixture(),
  ...overrides,
});

/** A brand that has published but has no scheduled backgrounds. */
export const publishedHomeConfigFixture = (): HomeConfig => ({
  background: {
    default: { media: imageMediaFixture() },
    schedules: [],
  },
  blocks: { signedIn: null, signedOut: null },
});

export const actorFixture = (
  overrides?: Partial<AppHomeActor>,
): AppHomeActor => ({
  adminId: 'adm_2210',
  name: 'Tess Starr',
  system: false,
  ...overrides,
});

export const previewFixture = (
  overrides?: Partial<AppHomePreview>,
): AppHomePreview => ({
  enabled: true,
  userIds: ['usr_91kd', 'usr_2m4p'],
  asOf: null,
  /*
   * The server does not send this yet, so the mock is where the field exists at all.
   * Built here first for the same reason the rest of this feature was: the day it lands
   * upstream there is nothing left to do.
   */
  stack: 'signedIn',
  startedBy: actorFixture(),
  startedAt: '2026-07-29T14:05:00Z',
  updatedAt: '2026-07-29T14:05:00Z',
  ...overrides,
});

export const appHomeConfigFixture = (
  overrides?: Partial<AppHomeConfigEnvelope>,
): AppHomeConfigEnvelope => ({
  clientId: 'daybreak-coffee',
  version: 14,
  hasUnpublishedChanges: true,
  publishedAt: '2026-07-22T18:04:11Z',
  publishedBy: actorFixture({ adminId: 'adm_4471', name: 'Lexi Sprague' }),
  draftSavedAt: '2026-07-29T13:51:02Z',
  draftSavedBy: actorFixture(),
  live: publishedHomeConfigFixture(),
  draft: homeConfigFixture(),
  preview: previewFixture({ enabled: false }),
  ...overrides,
});

/** Never configured — the empty state the setup screen renders. */
export const emptyAppHomeConfigFixture = (): AppHomeConfigEnvelope =>
  appHomeConfigFixture({
    version: 0,
    hasUnpublishedChanges: false,
    publishedAt: null,
    publishedBy: null,
    draftSavedAt: null,
    draftSavedBy: null,
    live: null,
    draft: null,
  });

/**
 * Published with nothing in flight. The draft is a copy of live rather than null —
 * publishing levels the draft, it does not remove it.
 */
export const cleanAppHomeConfigFixture = (): AppHomeConfigEnvelope =>
  appHomeConfigFixture({
    hasUnpublishedChanges: false,
    draft: homeConfigFixture(),
    live: homeConfigFixture(),
  });

/**
 * A publish the server refused, with every rule that failed. The codes, messages
 * and labels are the server's own, quoted from `AppHomeConfigPublishValidator` —
 * a fixture that invents them tests the portal against a server that does not
 * exist. Overlap used to be the second entry and is not a rule at all: two open
 * windows are legal, and the first eligible row wins.
 */
export const publishRejectionFixture = (
  overrides?: Partial<PublishRejection>,
): PublishRejection => ({
  code: 'VALIDATION_FAILED',
  message: '2 issues to resolve before publishing.',
  errors: [
    {
      code: 'ALT_TEXT_REQUIRED',
      message: 'Add alt text describing this background.',
      path: '/background/default/media/altText',
      label: 'Default home screen',
    },
    {
      code: 'INVALID_DATE_RANGE',
      message: 'End must be after start.',
      path: '/background/schedules/1/end',
      label: 'Weekend brunch',
    },
  ],
  ...overrides,
});

export const auditEntryFixture = (
  overrides?: Partial<AppHomeAuditEntry>,
): AppHomeAuditEntry => ({
  id: 'aud_0091',
  timestamp: '2026-07-29T14:02:40Z',
  action: 'PUBLISHED',
  version: 15,
  actor: actorFixture(),
  note: 'Swapped the hero for the summer menu shot and queued the holiday loop for December.',
  changedFieldCount: 6,
  ...overrides,
});

export const auditEntriesFixture = (): AppHomeAuditEntry[] => [
  auditEntryFixture(),
  auditEntryFixture({
    id: 'aud_0090',
    timestamp: '2026-07-29T13:51:02Z',
    action: 'DRAFT_SAVED',
    version: null,
    note: 'Roughed out the July 4th background — still waiting on final artwork.',
    changedFieldCount: 4,
  }),
  auditEntryFixture({
    id: 'aud_0089',
    timestamp: '2026-07-22T18:04:11Z',
    action: 'PUBLISHED',
    version: 14,
    actor: actorFixture({ adminId: 'adm_4471', name: 'Lexi Sprague' }),
    note: 'Initial launch of the configurable home screen.',
    changedFieldCount: 11,
  }),
];

export const auditSnapshotsFixture = (
  overrides?: Partial<AppHomeAuditSnapshots>,
): AppHomeAuditSnapshots => ({
  id: 'aud_0091',
  action: 'PUBLISHED',
  configBefore: publishedHomeConfigFixture(),
  configAfter: homeConfigFixture(),
  ...overrides,
});
